import React from "react";
import moment from "moment";
import { withRouter } from "react-router";
import Page from "../../components/shared/pages/Page";
import Loading from "../../components/shared/loaders/Loading";
import OrganizationSideMenu from "../organization/OrganizationSideMenu";
import store from "../../Store";
import { setDashboardMenu } from "../../routes/main-route/MainRoute-Actions";
import {
  fetchPlans,
  fetchOrgSlots,
  fetchPendingSites,
  reduceSlots,
  isNotDeployed,
  unwrapList,
  unwrapObject,
} from "./billingApi";

const STATUS_TONE = {
  active: "ok",
  past_due: "warn",
  canceled: "err",
  incomplete: "off",
  selected: "info",
};

const PENDING_TONE = { pending_payment: "warn", payment_failed: "err" };
const PENDING_LABEL = {
  pending_payment: "pending payment",
  payment_failed: "payment failed",
};

// Org-scoped billing (replaces the legacy Payments page — invoices/receipts
// live in Stripe now). Shows this organization's subscription status (one
// row per plan tier — an org can hold slots on more than one plan at once)
// and any of its sites blocked on payment.
class OrgBilling extends React.Component {
  state = {
    loading: true,
    notDeployed: false,
    error: "",
    subscriptionStatus: null,
    stripeSubscriptionId: null,
    currentPeriodEnd: null,
    tiers: [],
    pending: [],
    planPrices: {},
    planIds: {},
    reduceOpenKey: null,
    reduceQty: "",
    reduceReason: "",
    reduceBusy: false,
    reduceError: null,
  };

  orgId() {
    // route: /organizations/:id/payments
    const parts = this.props.location.pathname.split("/");
    return parseInt(parts[2], 10);
  }

  componentDidMount() {
    document.querySelector("body").classList.add("organization-dashboard");
    store.dispatch(setDashboardMenu(""));

    fetchPlans()
      .then((res) => {
        const planPrices = {};
        const planIds = {};
        unwrapList(res).forEach((p) => {
          if (!p.slug) return;
          planIds[p.slug] = p.id;
          planPrices[p.slug] = {};
          (p.prices || []).forEach((pr) => {
            planPrices[p.slug][(pr.currency || "").toUpperCase()] = pr.amount;
          });
        });
        this.setState({ planPrices, planIds });
      })
      .catch(() => {});

    this.loadSubscriptions();
  }

  componentWillUnmount() {
    document.querySelector("body").classList.remove("organization-dashboard");
  }

  loadSubscriptions() {
    const orgId = this.orgId();
    this.setState({ loading: true });
    return Promise.all([fetchOrgSlots(orgId), fetchPendingSites()])
      .then(([slotsRes, pendingRes]) => {
        const slots = unwrapObject(slotsRes);
        const pending = unwrapList(pendingRes).filter(
          (s) => s.organization_id === orgId,
        );
        this.setState({
          subscriptionStatus: slots.subscription_status || null,
          stripeSubscriptionId: slots.stripe_subscription_id || null,
          currentPeriodEnd: slots.current_period_end || null,
          tiers: slots.tiers || [],
          pending,
          loading: false,
        });
      })
      .catch((err) => {
        if (isNotDeployed(err)) {
          this.setState({ notDeployed: true, loading: false });
        } else {
          this.setState({
            error: "Could not load billing status.",
            loading: false,
          });
        }
      });
  }

  mrr(tier) {
    if (!tier || !tier.paid_slots) return "—";
    const currency = "SEK";
    const amounts = this.state.planPrices[tier.slug] || {};
    const price = amounts[currency];
    if (price == null) return `${tier.paid_slots} × ?`;
    return `${tier.paid_slots} × ${parseFloat(price)} kr`;
  }

  reduceKey(tier) {
    return tier.plan_id != null ? tier.plan_id : tier.slug;
  }

  planId(tier) {
    return tier.plan_id != null ? tier.plan_id : this.state.planIds[tier.slug];
  }

  freeSlots(tier) {
    if (tier.free_slots != null) return tier.free_slots;
    const paid = tier.paid_slots != null ? tier.paid_slots : 0;
    const live = tier.live_sites != null ? tier.live_sites : 0;
    return Math.max(paid - live, 0);
  }

  openReduce = (tier) => {
    this.setState({
      reduceOpenKey: this.reduceKey(tier),
      reduceQty: String(this.freeSlots(tier)),
      reduceReason: "",
      reduceError: null,
    });
  };

  closeReduce = () => {
    this.setState({ reduceOpenKey: null, reduceError: null });
  };

  submitReduce = (tier) => {
    const orgId = this.orgId();
    const planId = this.planId(tier);
    const freeSlots = this.freeSlots(tier);
    const reason = this.state.reduceReason.trim();

    if (this.state.reduceQty.trim() === "") {
      this.setState({ reduceError: "Quantity is required." });
      return;
    }
    const amount = parseInt(this.state.reduceQty, 10);
    if (!planId || Number.isNaN(amount)) return;
    if (!reason) {
      this.setState({ reduceError: "Reason is required." });
      return;
    }
    if (amount < 0) {
      this.setState({ reduceError: "Quantity cannot be negative." });
      return;
    }
    if (amount > freeSlots) {
      const paidSlots =
        tier.paid_slots != null
          ? tier.paid_slots
          : freeSlots + (tier.live_sites != null ? tier.live_sites : 0);
      const liveSites =
        tier.live_sites != null
          ? tier.live_sites
          : Math.max(paidSlots - freeSlots, 0);
      this.setState({
        reduceError: `Cannot reduce ${amount} slot(s): only ${freeSlots} idle slot(s) on this tier (${paidSlots} paid, ${liveSites} occupied by live sites).`,
      });
      return;
    }

    this.setState({ reduceBusy: true, reduceError: null });
    reduceSlots(orgId, {
      plan_id: planId,
      quantity: amount,
      reason,
    })
      .then(() => {
        this.setState({
          reduceBusy: false,
          reduceOpenKey: null,
          reduceQty: "",
          reduceReason: "",
        });
        this.loadSubscriptions();
      })
      .catch((err) => {
        const status = err && err.response && err.response.status;
        const message =
          (err.response && err.response.data && err.response.data.message) ||
          (status === 422
            ? "Cannot reduce more than this plan's free slots."
            : "Could not reduce slots.");
        this.setState({ reduceBusy: false, reduceError: message });
      });
  };

  render() {
    const {
      loading,
      notDeployed,
      error,
      subscriptionStatus,
      stripeSubscriptionId,
      currentPeriodEnd,
      tiers,
      pending,
      reduceOpenKey,
      reduceQty,
      reduceReason,
      reduceBusy,
      reduceError,
    } = this.state;
    const st = subscriptionStatus || (tiers.length > 0 ? "selected" : "—");

    return (
      <Page title="Billing">
        <div className="page-content">
          <div className="organizations-sidebar">
            <OrganizationSideMenu processing={loading} />
          </div>
          <div className="content-section">
            <div className="heading-block pr-60">
              <div className="title-block">
                <h2>Billing</h2>
                <p className="billing-sub">
                  This organization&rsquo;s subscription, mirrored from Stripe.
                  Invoices, receipts and dunning live in the Stripe dashboard.
                </p>
              </div>
            </div>

            {notDeployed ? (
              <div className="billing-notice">
                The billing endpoints are not deployed yet (portal-api&nbsp;·{" "}
                <code>stripe-ground-zero</code>).
              </div>
            ) : error ? (
              <div className="billing-notice billing-notice--error">
                {error}
              </div>
            ) : null}

            <div className="table-block table-responsive pr-60">
              <table className="table">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Sites (qty)</th>
                    <th>MRR</th>
                    <th>Current period ends</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center">
                        <Loading />
                      </td>
                    </tr>
                  ) : tiers.length > 0 ? (
                    tiers.map((tier) => {
                      const key = this.reduceKey(tier);
                      const reduceOpen = reduceOpenKey === key;
                      return (
                        <React.Fragment key={key}>
                          <tr>
                            <td>{tier.title || tier.slug || "—"}</td>
                            <td>
                              <span
                                className={`billing-badge ${STATUS_TONE[st] || "off"}`}
                              >
                                {st}
                              </span>
                              {st === "selected" && (
                                <div className="billing-muted">
                                  plan chosen, no live subscription yet
                                </div>
                              )}
                            </td>
                            <td>
                              {tier.paid_slots != null
                                ? `${tier.live_sites != null ? tier.live_sites : 0}/${tier.paid_slots} used`
                                : "—"}
                            </td>
                            <td>{this.mrr(tier)}</td>
                            <td>
                              {currentPeriodEnd
                                ? moment(currentPeriodEnd).format("YYYY-MM-DD")
                                : "—"}
                            </td>
                            <td>
                              <span
                                style={{
                                  display: "inline-flex",
                                  gap: "8px",
                                  alignItems: "center",
                                }}
                              >
                                {stripeSubscriptionId ? (
                                  <a
                                    href={`https://dashboard.stripe.com/subscriptions/${stripeSubscriptionId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="billing-link-btn"
                                    title="Invoices, receipts and dunning in Stripe"
                                  >
                                    ↗ Stripe
                                  </a>
                                ) : null}
                                {this.freeSlots(tier) > 0 && (
                                  <button
                                    type="button"
                                    className="billing-link-btn"
                                    onClick={() =>
                                      reduceOpen
                                        ? this.closeReduce()
                                        : this.openReduce(tier)
                                    }
                                  >
                                    Reduce slots
                                  </button>
                                )}
                              </span>
                            </td>
                          </tr>
                          {reduceOpen && (
                            <tr className="billing-form-row">
                              <td colSpan={6}>
                                <div className="billing-inline-form">
                                  <label>
                                    Slots to reduce
                                    <input
                                      type="number"
                                      required
                                      min={0}
                                      max={this.freeSlots(tier)}
                                      className="form-control"
                                      value={reduceQty}
                                      onChange={(e) =>
                                        this.setState({
                                          reduceQty: e.target.value,
                                        })
                                      }
                                    />
                                  </label>
                                  <label>
                                    Reason
                                    <input
                                      type="text"
                                      required
                                      className="form-control"
                                      value={reduceReason}
                                      onChange={(e) =>
                                        this.setState({
                                          reduceReason: e.target.value,
                                        })
                                      }
                                      placeholder="e.g. support exit — 0 live sites"
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    className="btn-theme btn-dark-blue"
                                    disabled={reduceBusy}
                                    onClick={() => this.submitReduce(tier)}
                                  >
                                    {reduceBusy
                                      ? "Saving…"
                                      : "Confirm reduction"}
                                  </button>
                                  <button
                                    type="button"
                                    className="billing-link-btn"
                                    disabled={reduceBusy}
                                    onClick={this.closeReduce}
                                  >
                                    Cancel
                                  </button>
                                  <span className="billing-muted">
                                    Only unused slots can be reduced — maximum{" "}
                                    {this.freeSlots(tier)} free slot
                                    {this.freeSlots(tier) === 1 ? "" : "s"}{" "}
                                    available on this plan.
                                  </span>
                                  {reduceError && (
                                    <span
                                      className="billing-muted"
                                      style={{ color: "#d23f4a" }}
                                    >
                                      {reduceError}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : !notDeployed ? (
                    <tr>
                      <td colSpan={6} className="text-center">
                        No subscription for this organization
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {pending.length > 0 && (
              <>
                <div className="heading-block pr-60">
                  <div className="title-block">
                    <h2>Sites awaiting payment</h2>
                  </div>
                </div>
                <div className="table-block table-responsive pr-60">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Site</th>
                        <th>Status</th>
                        <th>Pending invoice</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {pending.map((row, i) => {
                        const ps = row.provision_status || "—";
                        return (
                          <tr key={i}>
                            <td>
                              {row.site_name ||
                                row.site ||
                                row.display_name ||
                                "—"}
                            </td>
                            <td>
                              <span
                                className={`billing-badge ${PENDING_TONE[ps] || "off"}`}
                              >
                                {PENDING_LABEL[ps] || ps}
                              </span>
                            </td>
                            <td>
                              <code>{row.pending_invoice_id || "—"}</code>
                            </td>
                            <td className="text-center">
                              {row.pending_invoice_id ? (
                                <a
                                  href={`https://dashboard.stripe.com/invoices/${row.pending_invoice_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="billing-link-btn"
                                >
                                  ↗ Invoice
                                </a>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </Page>
    );
  }
}

export default withRouter(OrgBilling);
